import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useProfile } from "@/hooks/useProfile";
import { useSubscription } from "@/hooks/useSubscription";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { LayoutDashboard, Package, Sparkles, Settings, LogOut, Crown } from "lucide-react";
import PrimaryFAB from "./PrimaryFAB";
import TrialExpiredScreen from "./TrialExpiredScreen";
import manifestLogo from "@/assets/manifest-logo.webp";
import { PRO_TIER_ENABLED } from "@/lib/featureFlags";

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const { signOut } = useAuth();
  const { data: profile } = useProfile();
  const { isPro, isTrial, entitlements, isLoading: subLoading, isError: subError } = useSubscription();
  const location = useLocation();
  const navigate = useNavigate();
  const [isPWA, setIsPWA] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);

  const pathname = location.pathname;
  const isTabActive = (route: string) => {
    if (route === "/dashboard") return pathname === "/dashboard" || pathname === "/";
    return pathname.startsWith(route);
  };

  useEffect(() => {
    setIsPWA(window.matchMedia("(display-mode: standalone)").matches);
  }, []);

  const navItems = [
    { to: "/dashboard", icon: LayoutDashboard, label: "Home" },
    { to: "/assets", icon: Package, label: "Assets" },
    { to: "/insights", icon: Sparkles, label: "Insights" },
    { to: "/settings", icon: Settings, label: "Settings" },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Top header */}
      <header className="sticky top-0 z-50 border-b border-border bg-card/80 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-[430px] items-center justify-between px-4 md:max-w-6xl">
          <Link to="/dashboard" className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-lg overflow-hidden flex-shrink-0">
              <img src={manifestLogo} alt="Manifest" className="h-full w-full object-contain" />
            </div>
            <span className="font-display text-2xl font-bold text-foreground">Manifest</span>
            {PRO_TIER_ENABLED && isPro && (
              entitlements.tier === "unlimited" ? (
                <Badge className="bg-violet-500/15 text-violet-600 border-violet-500/30 gap-1 text-[10px] px-1.5 py-0.5 hover:bg-violet-500/15">
                  <Sparkles className="h-3 w-3" /> Unlimited
                </Badge>
              ) : (
                <Badge className="bg-amber-500/15 text-amber-600 border-amber-500/30 gap-1 text-[10px] px-1.5 py-0.5 hover:bg-amber-500/15">
                  <Crown className="h-3 w-3" /> Vault{isTrial ? " (Trial)" : ""}
                </Badge>
              )
            )}
          </Link>

          <nav className="hidden items-center gap-1 md:flex">
            {navItems.map((item) => (
              <Link key={item.to} to={item.to}>
                <Button
                  variant={isTabActive(item.to) ? "default" : "ghost"}
                  size="sm"
                  className="gap-2"
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </Button>
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {/* Mobile logout — hidden in PWA */}
            {!isPWA && (
              <Button variant="ghost" size="icon" onClick={() => setLogoutConfirmOpen(true)} aria-label="Sign out" className="min-h-[44px] min-w-[44px] md:hidden">
                <LogOut className="h-4 w-4" />
              </Button>
            )}
            <div className="hidden items-center gap-2 md:flex">
              <span className="text-sm text-muted-foreground font-body">{profile?.display_name}</span>
              <Button variant="ghost" size="icon" onClick={() => setLogoutConfirmOpen(true)} aria-label="Sign out" className="min-h-[44px] min-w-[44px]">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-card/95 backdrop-blur-md md:hidden safe-area-bottom">
        <div className="mx-auto flex max-w-[430px] items-center justify-around py-1.5">
          {navItems.map((item) => {
            const isActive = isTabActive(item.to);
            return (
              <Link key={item.to} to={item.to}>
                <button
                  className={`flex min-h-[44px] min-w-[44px] flex-col items-center justify-center gap-0.5 rounded-lg px-3 py-1 transition-colors ${
                    isActive ? "text-primary" : "text-muted-foreground"
                  }`}
                >
                  <item.icon className="h-5 w-5" />
                  <span className="text-[10px] font-medium">{item.label}</span>
                </button>
              </Link>
            );
          })}
          
        </div>
      </nav>

      {/* Main content */}
      <main className="mx-auto max-w-[430px] px-4 py-6 pb-28 md:max-w-6xl md:pb-6">
        {children}
      </main>

      {/* Trial expired overlay — don't show when subscription check errored */}
      {PRO_TIER_ENABLED && !subLoading && !subError && entitlements.tier === 'expired' && <TrialExpiredScreen />}

      {/* FAB */}
      <PrimaryFAB />

      {/* Logout confirmation */}
      <AlertDialog open={logoutConfirmOpen} onOpenChange={setLogoutConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sign out?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to sign out of your account?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                await signOut();
                navigate("/");
              }}
            >
              Sign out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
